import React from 'react';
import {
  Routes,
  Route,
  useLocation,
  useMatch,
  useNavigate,
} from 'react-router-dom';

import { FiMaximize2 } from 'react-icons/fi';
import Select from 'react-select';

import { MainRoutes, ReportRoutes } from 'helpers/routes';
import useMountEffect from 'hooks/useMountEffect';
import useReports from 'hooks/useReports';
import useUpdateEffect from 'hooks/useUpdateEffect';
import useUser from 'hooks/useUser';

import { LinkBackButton } from 'components/elements/BackButton';
import SectionWrapper from 'components/layout/SectionWrapper';

import HomePage from './HomePage';
import {
  ReportFullscreenProvider,
  useReportFullscreen,
} from './ReportFullscreenContext';
import ReportDisplayPage from './ReportDisplayPage';
import WorkspacePage from './WorkspacePage';
import {
  REPORT_VIEW_CONTROL_BORDER_RADIUS,
  REPORT_VIEW_CONTROL_HEIGHT,
  ReportViewHeaderControls,
  ReportViewRoutes,
  ReportViewToggleButton,
} from './reportsViewStyles';

function ReportViewPageContent() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const reportMatch = useMatch(
    `${MainRoutes.REPORTS}/${ReportRoutes.VIEW}/:reportId`,
  );
  const isWorkspaceRoute = reportMatch?.params.reportId === 'workspace';
  const isReportDetailRoute = !!reportMatch?.params.reportId && !isWorkspaceRoute;
  const { enterReportFullscreen, exitReportFullscreen, isReportFullscreen } = useReportFullscreen();
  const {
    fetchUsers,
    fetchReports,
    hasWorkSpace,
    impersonateUserId,
    reports,
    selectedOption,
    userOptions,
  } = useReports();
  const { appUser, isBdataAmin } = useUser();

  const homeReportId = React.useMemo(
    () => reports?.data?.filter((x) => x.group === 'Home')?.[0]?.id,
    [reports],
  );

  const showFullscreenToggle = React.useMemo(
    () => !!homeReportId || isReportDetailRoute || isWorkspaceRoute,
    [homeReportId, isReportDetailRoute, isWorkspaceRoute],
  );

  React.useEffect(() => {
    exitReportFullscreen();
  }, [exitReportFullscreen, pathname]);

  const ReportViewHeaderMenu = React.useCallback(() => {
    const onImpersonateUser = (id) => {
      navigate(
        `${MainRoutes.REPORTS}/${ReportRoutes.VIEW}?impersonateUserId=${id}`,
        { replace: true },
      );
    };

    const showImpersonateSelect = isBdataAmin;

    if (!showFullscreenToggle && !showImpersonateSelect) return null;

    return (
      <ReportViewHeaderControls>
        {showImpersonateSelect && (
          <Select
            placeholder="Select a user to view report as"
            options={userOptions}
            onChange={({ value }) => onImpersonateUser(value)}
            value={selectedOption}
            styles={{
              container: (baseStyles) => ({ ...baseStyles, width: '20rem' }),
              control: (baseStyles) => ({
                ...baseStyles,
                borderRadius: REPORT_VIEW_CONTROL_BORDER_RADIUS,
                minHeight: REPORT_VIEW_CONTROL_HEIGHT,
              }),
            }}
          />
        )}
        {showFullscreenToggle && !isReportFullscreen && (
          <ReportViewToggleButton
            aria-label="View report in full screen"
            title="Full screen"
            type="button"
            onClick={enterReportFullscreen}
          >
            <FiMaximize2 size={18} />
          </ReportViewToggleButton>
        )}
      </ReportViewHeaderControls>
    );
  }, [
    enterReportFullscreen,
    isBdataAmin,
    isReportFullscreen,
    navigate,
    selectedOption,
    showFullscreenToggle,
    userOptions,
  ]);

  const fetchReportsCallback = React.useCallback(() => {
    fetchReports({
      userRequesting: appUser?.id,
      impersonateUserId: impersonateUserId ? Number(impersonateUserId) : null,
    });
  }, [appUser, fetchReports, impersonateUserId]);

  useMountEffect(() => {
    fetchReportsCallback();
    if (isBdataAmin) fetchUsers();
  });

  useUpdateEffect(() => {
    fetchReportsCallback();
  }, [impersonateUserId]);

  const showListingBackButton = isReportDetailRoute || isWorkspaceRoute;

  const listingBackButton = showListingBackButton ? (
    <LinkBackButton
      to={{
        pathname: `${MainRoutes.REPORTS}/${ReportRoutes.VIEW}`,
        search,
      }}
    />
  ) : null;

  return (
    <SectionWrapper
      backButton={listingBackButton}
      fillHeight
      goBack={isBdataAmin && !showListingBackButton}
      menu={<ReportViewHeaderMenu />}
    >
      <ReportViewRoutes>
        <Routes>
          <Route exact path="/" element={<HomePage />} />
          {hasWorkSpace && (
            <Route path="workspace" element={<WorkspacePage />} />
          )}
          <Route path=":reportId" element={<ReportDisplayPage />} />
        </Routes>
      </ReportViewRoutes>
    </SectionWrapper>
  );
}

export default function ReportViewPage() {
  return (
    <ReportFullscreenProvider>
      <ReportViewPageContent />
    </ReportFullscreenProvider>
  );
}
